import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import {
  adminLoginSchema,
  normalizePastedTotpCode,
  toAuthenticateAdminRequest,
} from "../schema";
import type { AdminLoginFormValues } from "../schema";
import type { Schemas } from "../../workspace/api.generated";

export interface LoginFeedback {
  readonly title: string;
  readonly message: string;
  readonly variant?: "success" | "warning";
}

export type LoginSubmitResult = "authenticated" | "second-factor" | "rejected";

export interface LoginViewProps {
  readonly error?: LoginFeedback | null;
  readonly notice?: LoginFeedback | null;
  readonly onClearError?: () => void;
  readonly onSubmit: (input: Schemas["AuthenticateAdminRequest"]) => Promise<LoginSubmitResult>;
}

export function LoginView({ error, notice, onClearError, onSubmit }: LoginViewProps) {
  const submitLock = useRef(false);
  const [stage, setStage] = useState<"credentials" | "second-factor">("credentials");
  const form = useForm<AdminLoginFormValues>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: {
      loginId: "",
      password: "",
      secondFactorMode: "totp",
      totpCode: "",
      recoveryCode: "",
    },
    shouldFocusError: true,
  });
  const submitting = form.formState.isSubmitting || submitLock.current;

  const submit = form.handleSubmit(async (values) => {
    if (submitLock.current) return;
    submitLock.current = true;
    onClearError?.();
    try {
      if (stage === "second-factor" && values.secondFactorMode === "totp" && !/^\d{6}$/.test(values.totpCode)) {
        form.setError("totpCode", { message: "TOTP 코드는 숫자 6자리로 입력해 주세요." });
        form.setFocus("totpCode");
        return;
      }
      const request = toAuthenticateAdminRequest(values);
      const result = await onSubmit(stage === "credentials"
        ? { ...request, totpCode: null, recoveryCode: null }
        : request);
      if (result === "authenticated") form.reset();
      if (result === "second-factor") {
        setStage("second-factor");
        form.setValue("secondFactorMode", "totp");
        form.setValue("totpCode", "");
        form.setValue("recoveryCode", "");
        queueMicrotask(() => form.setFocus("totpCode"));
      }
    } finally {
      submitLock.current = false;
    }
  });

  return (
    <>
      <h1>{stage === "credentials" ? "관리자 로그인" : form.watch("secondFactorMode") === "totp" ? "인증번호 입력" : "복구코드 확인"}</h1>
      <p>{stage === "credentials"
        ? "계정 정보를 확인한 뒤 필요한 보안 단계만 이어서 안내합니다."
        : form.watch("secondFactorMode") === "totp"
          ? "인증 앱에 표시된 현재 숫자 6자리를 입력해 주세요."
          : "TOTP 등록 때 보관한 일회용 복구코드를 입력합니다."}</p>
      {notice ? (
        <Alert persistent variant={notice.variant ?? "success"} title={notice.title}>
          {notice.message}
        </Alert>
      ) : null}
      {error ? (
        <Alert persistent variant="danger" title={error.title}>
          {error.message}
        </Alert>
      ) : null}
      <form noValidate onSubmit={submit}>
        {stage === "credentials" ? <>
          <HookFormField
            autoComplete="username"
            formControl={form.control}
            label="관리자 계정"
            maxLength={100}
            name="loginId"
            required
          />
          <HookFormField
            autoComplete="current-password"
            formControl={form.control}
            label="비밀번호"
            maxLength={200}
            name="password"
            required
            type="password"
          />
        </> : form.watch("secondFactorMode") === "totp" ? (
          <HookFormField
            autoComplete="one-time-code"
            formControl={form.control}
            inputMode="numeric"
            label="TOTP 코드"
            maxLength={6}
            name="totpCode"
            required
            onPaste={(event) => {
              const normalized = normalizePastedTotpCode(event.clipboardData.getData("text"));
              if (!normalized) return;
              event.preventDefault();
              form.setValue("totpCode", normalized, { shouldDirty: true, shouldValidate: true });
            }}
            pattern="[0-9]*"
          />
        ) : (
          <HookFormField
            autoComplete="one-time-code"
            description="TOTP 등록 때 보관한 일회용 복구코드를 입력합니다. 사용 후 인증 앱을 다시 등록합니다."
            formControl={form.control}
            label="복구코드"
            maxLength={64}
            name="recoveryCode"
            required
          />
        )}
        {stage === "second-factor" ? <Button
            size="small"
            type="button"
            variant="quiet"
            onClick={() => {
              const nextMode = form.getValues("secondFactorMode") === "totp" ? "recovery" : "totp";
              form.setValue("secondFactorMode", nextMode);
              form.setValue(nextMode === "totp" ? "recoveryCode" : "totpCode", "", { shouldValidate: false });
              form.clearErrors(["totpCode", "recoveryCode"]);
              onClearError?.();
              queueMicrotask(() => form.setFocus(nextMode === "totp" ? "totpCode" : "recoveryCode"));
            }}
          >
            {form.watch("secondFactorMode") === "totp" ? "인증 앱을 사용할 수 없나요?" : "인증 앱 코드로 로그인"}
          </Button> : null}
        <Button
          className="admin-auth__submit"
          isLoading={submitting}
          loadingLabel="로그인 처리 중"
          size="large"
          type="submit"
        >
          {stage === "credentials" ? "로그인" : "인증하고 로그인"}
        </Button>
      </form>
      {stage === "second-factor" ? <Button size="small" type="button" variant="quiet" onClick={() => { setStage("credentials"); onClearError?.(); queueMicrotask(() => form.setFocus("loginId")); }}>다른 계정으로 로그인</Button> : null}
      <p className="admin-auth__help">{stage === "credentials" ? "처음 로그인하는 계정은 비밀번호 변경과 인증 앱 등록 화면으로 자동 이동합니다." : "로그인 시도와 접속 IP가 감사로그에 기록됩니다."}</p>
    </>
  );
}
